// frontend/src/components/ui/action-buttons.jsx
import React from 'react';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';
import { getButtonClasses } from '../../utils/brandingUtils';
import {
    EnvelopeIcon,
    PencilIcon,
    EyeIcon,
    MapPinIcon,
    TrashIcon,
    BuildingOfficeIcon,
    PlusIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Action button variants with semantic meaning - will be generated dynamically with branding
const getActionVariants = (branding) => {
    return {
        primary: {
            className: "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90",
            iconColor: "text-white"
        },
        secondary: {
            className: "bg-card text-foreground border-input hover:bg-muted",
            iconColor: "text-foreground"
        },
        success: {
            className: "bg-[var(--brand-secondary)] text-white border-[var(--brand-secondary)] hover:bg-[var(--brand-secondary)]/90",
            iconColor: "text-white"
        },
        warning: {
            className: "bg-[var(--brand-accent)] text-white border-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90",
            iconColor: "text-white"
        },
        danger: {
            className: "bg-red-600 text-white border-red-600 hover:bg-red-700",
            iconColor: "text-white"
        },
        info: {
            className: "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/20",
            iconColor: "text-[var(--brand-primary)]"
        },
        dangerOutline: {
            className: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
            iconColor: "text-red-700"
        }
    };
};

// Action types with their configurations
const ACTION_TYPES = {
    sendEmail: {
        icon: EnvelopeIcon,
        label: "Send Email",
        variant: "info",
        priority: "high",
        description: "Send email communication to client"
    },
    edit: {
        icon: PencilIcon,
        label: "Edit",
        variant: "secondary",
        priority: "high",
        description: "Edit client information"
    },
    view: {
        icon: EyeIcon,
        label: "View",
        variant: "primary",
        priority: "medium",
        description: "View detailed information"
    },
    workspace: {
        icon: BuildingOfficeIcon,
        label: "Workspace",
        variant: "primary",
        priority: "high",
        description: "Open client workspace"
    },
    journey: {
        icon: MapPinIcon,
        label: "Journey",
        variant: "warning",
        priority: "high",
        description: "View relocation journey"
    },
    delete: {
        icon: TrashIcon,
        label: "Delete",
        variant: "dangerOutline",
        priority: "low",
        description: "Delete client record",
        requiresConfirmation: true
    },
    add: {
        icon: PlusIcon,
        label: "Add",
        variant: "success",
        priority: "high",
        description: "Add new item"
    },
    qualify: {
        icon: CheckCircleIcon,
        label: "Qualify",
        variant: "success",
        priority: "high",
        description: "Qualify lead as potential client"
    },
    nurture: {
        icon: ClockIcon,
        label: "Nurture",
        variant: "secondary",
        priority: "medium",
        description: "Add to nurture campaign"
    }
};

// Enhanced Action Button Component
export const ActionButton = ({
    actionType,
    onClick,
    disabled = false,
    loading = false,
    size = "sm",
    showLabel = true,
    showIcon = true,
    className = "",
    children,
    ...props
}) => {
    const { branding } = useBranding();
    const action = ACTION_TYPES[actionType];
    if (!action) {
        console.warn(`Unknown action type: ${actionType}`);
        return null;
    }

    const variants = getActionVariants(branding);
    const variant = variants[action.variant];
    const Icon = action.icon;

    return (
        <Button
            onClick={onClick}
            disabled={disabled || loading}
            size={size}
            className={cn(
                "flex items-center gap-2 transition-all duration-200",
                variant.className,
                className
            )}
            title={action.description}
            aria-label={action.description}
            {...props}
        >
            {showIcon && (
                <Icon className={cn("h-4 w-4", variant.iconColor)} />
            )}
            {showLabel && action.label}
            {children}
            {loading && (
                <div className="ml-1 h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            )}
        </Button>
    );
};

// Action Button Group Component
export const ActionButtonGroup = ({
    actions = [],
    context = "client", // client, account, lead, task
    onAction,
    disabled = false,
    loading = {},
    className = "",
    maxVisible = 4,
    showLabels = true,
    responsive = true
}) => {
    const { branding } = useBranding();
    // Filter actions based on context and priority
    const getContextualActions = () => {
        const contextActions = {
            client: ['sendEmail', 'edit', 'workspace', 'journey', 'delete'],
            account: ['sendEmail', 'edit', 'workspace', 'delete'],
            lead: ['sendEmail', 'edit', 'qualify', 'nurture', 'delete'],
            task: ['edit', 'view', 'delete']
        };

        return contextActions[context] || contextActions.client;
    };

    const availableActions = getContextualActions();
    const filteredActions = actions.filter(action => 
        availableActions.includes(action.type)
    );

    // Sort by priority (high -> medium -> low)
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const sortedActions = filteredActions.sort((a, b) => {
        const aPriority = ACTION_TYPES[a.type]?.priority || 'medium';
        const bPriority = ACTION_TYPES[b.type]?.priority || 'medium';
        return priorityOrder[bPriority] - priorityOrder[aPriority];
    });

    // Separate primary and secondary actions
    const primaryActions = sortedActions.slice(0, maxVisible);
    const secondaryActions = sortedActions.slice(maxVisible);

    return (
        <div className={cn("flex items-center gap-2", className)}>
            {/* Primary Actions */}
            <div className="flex items-center gap-2">
                {primaryActions.map((action, index) => (
                    <ActionButton
                        key={action.type}
                        actionType={action.type}
                        onClick={() => onAction?.(action.type, action.data)}
                        disabled={disabled}
                        loading={loading[action.type]}
                        showLabel={showLabels}
                        className={cn(
                            index === 0 && "sm:order-first"
                        )}
                    />
                ))}
            </div>

            {/* Secondary Actions Dropdown */}
            {secondaryActions.length > 0 && (
                <div className="relative group">
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                        aria-label="More actions"
                    >
                        <span className="sr-only sm:not-sr-only">More</span>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </Button>
                    
                    <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <div className="py-1">
                            {secondaryActions.map((action) => (
                                <button
                                    key={action.type}
                                    onClick={() => onAction?.(action.type, action.data)}
                                    disabled={disabled}
                                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                                >
                                    {(() => {
                                        const IconComponent = ACTION_TYPES[action.type]?.icon;
                                        return IconComponent ? <IconComponent className="h-4 w-4" /> : null;
                                    })()}
                                    {ACTION_TYPES[action.type]?.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Quick Action Toolbar Component
export const QuickActionToolbar = ({
    entityType,
    entityId,
    entityData,
    onAction,
    className = ""
}) => {
    const getActionsForEntity = () => {
        const baseActions = [
            { type: 'sendEmail', data: { entityId, entityType } },
            { type: 'edit', data: { entityId, entityType } }
        ];

        switch (entityType) {
            case 'client':
                return [
                    ...baseActions,
                    { type: 'workspace', data: { entityId } },
                    { type: 'journey', data: { entityId } },
                    { type: 'delete', data: { entityId } }
                ];
            case 'account':
                return [
                    ...baseActions,
                    { type: 'workspace', data: { entityId } },
                    { type: 'delete', data: { entityId } }
                ];
            case 'lead':
                return [
                    ...baseActions,
                    { type: 'qualify', data: { entityId } },
                    { type: 'nurture', data: { entityId } },
                    { type: 'delete', data: { entityId } }
                ];
            default:
                return baseActions;
        }
    };

    return (
        <ActionButtonGroup
            actions={getActionsForEntity()}
            context={entityType}
            onAction={onAction}
            className={className}
            maxVisible={3}
        />
    );
};

// Confirmation Dialog Component for Destructive Actions
export const ActionConfirmationDialog = ({
    isOpen,
    onClose,
    onConfirm,
    actionType,
    entityName,
    entityType = "item"
}) => {
    const action = ACTION_TYPES[actionType];
    if (!action || !action.requiresConfirmation) return null;

    return (
        <div className={cn(
            "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50",
            isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}>
            <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-100 rounded-full">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                        Confirm {action.label}
                    </h3>
                </div>
                
                <p className="text-muted-foreground mb-6">
                    Are you sure you want to {action.label.toLowerCase()} <strong>{entityName}</strong>? 
                    This action cannot be undone.
                </p>
                
                <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="destructive" 
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                    >
                        {action.label}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { ACTION_TYPES };
